import { useDeferredValue, useState, type ReactNode } from 'react';
import { Archive, CloudOff, HardDrive, Server, ServerOff, Settings2 } from 'lucide-react';
import { Banner, Button, Card, CardGridSkeleton, EmptyState, Fact, Facts, PageHeader, ProgressBar, SearchInput, SubTabs } from '@/components/ui';
import { ErrorState } from '@/components/ErrorState';
import { BackupSettingsDialog } from '@/components/backups/BackupSettingsDialog';
import { RestoreToHostDialog } from '@/components/backups/RestoreToHostDialog';
import { SiteCopies } from '@/components/backups/SiteCopies';
import { useBackupSites, useBackupsOverview } from '@/hooks/useBackups';
import { useRoles } from '@/hooks/useAuth';
import { formatBytes, formatRelative } from '@/utils/format';
import { can } from '@/utils/permissions';
import type { BackupListItem, BackupServerGroup, BackupsOverview } from '@/types/api';

/** The server filter: one server, sites on none, or everything. */
type ServerFilter = string | 'none' | null;

function Storage({ overview }: { readonly overview: BackupsOverview }): ReactNode {
  const { storage } = overview;
  if (storage.driver === 's3') {
    return (
      <Card>
        <Facts>
          <Fact label="Где хранятся">S3-хранилище</Fact>
          <Fact label="Занимают">{formatBytes(storage.usedBytes)}</Fact>
        </Facts>
      </Card>
    );
  }
  const used = Number(storage.usedBytes);
  const free = storage.freeBytes;
  const low = free !== null && free < storage.minFreeBytes * 2;
  return (
    <Card>
      <div className="stack">
        <Facts>
          <Fact label="Где хранятся" icon={<HardDrive size={14} aria-hidden="true" />}>
            диск сервера панели
          </Fact>
          <Fact label="Занимают">{formatBytes(storage.usedBytes)}</Fact>
          <Fact label="Свободно">
            {free === null ? '—' : formatBytes(free)}
            <span className="muted"> · при остатке меньше {formatBytes(storage.minFreeBytes)} новые копии не принимаются</span>
          </Fact>
        </Facts>
        {free !== null ? <ProgressBar value={used} max={used + free} label="Копии на диске панели" tone={low ? 'down' : 'accent'} /> : null}
        <Banner tone={low ? 'danger' : 'warning'} icon={CloudOff}>
          {low ? 'Место на диске панели заканчивается. ' : ''}Копии хранятся только на сервере панели: подключите внешнее S3-хранилище (Cloudflare R2, Backblaze B2).
        </Banner>
      </div>
    </Card>
  );
}

function ServerTile({
  server,
  active,
  onPick,
  totals,
}: {
  readonly server: BackupServerGroup | null;
  readonly active: boolean;
  readonly onPick: () => void;
  /** For the «all servers» tile: everything added up. */
  readonly totals?: { readonly servers: number; readonly sites: number; readonly copies: number; readonly bytes: number; readonly problems: number };
}): ReactNode {
  const Icon = server === null ? Archive : server.id === null ? ServerOff : Server;
  return (
    <button type="button" className={`server-tile${active ? ' server-tile-active' : ''}`} aria-pressed={active} onClick={onPick}>
      <span className="row-between">
        <span className="row-tight strong truncate">
          <Icon size={15} aria-hidden="true" />
          {server ? server.name : 'Все серверы'}
        </span>
        {(server?.problems ?? totals?.problems ?? 0) > 0 ? (
          <span className="status status-down" title="Сайты с проблемами в копиях">
            {server?.problems ?? totals?.problems}
          </span>
        ) : null}
      </span>
      {server ? (
        <>
          <span className="text-sm muted">
            {server.role === 'host' ? 'внешний сервер' : server.role === 'node' ? 'сервер панели' : 'копий не будет, пока сервер не подключён'}
            {server.address ? <span className="mono"> · {server.address}</span> : null}
          </span>
          <span className="text-sm">
            сайтов {server.siteCount} · копий {server.copyCount} · {formatBytes(server.sizeBytes)}
          </span>
          <span className="text-sm muted">{server.lastCopyAt ? `последняя копия ${formatRelative(server.lastCopyAt)}` : 'проверенных копий нет'}</span>
        </>
      ) : totals ? (
        <>
          <span className="text-sm muted">серверов {totals.servers}</span>
          <span className="text-sm">
            сайтов {totals.sites} · копий {totals.copies} · {formatBytes(totals.bytes)}
          </span>
        </>
      ) : null}
    </button>
  );
}

/**
 * The Backups page by server (ADR-0056): where the copies are kept and how
 * much room is left, each server's share of them, then the sites — searched,
 * narrowed to one server or to those with a problem — each with its copies as
 * numbered slots, the owner's cap on how many a site keeps.
 */
export function BackupsPage(): ReactNode {
  const roles = useRoles();
  const overview = useBackupsOverview();
  const [server, setServer] = useState<ServerFilter>(null);
  const [search, setSearch] = useState('');
  const [problems, setProblems] = useState<'all' | 'problems'>('all');
  const q = useDeferredValue(search);
  const sites = useBackupSites({ serverId: server, q, problems: problems === 'problems' });
  const [restoring, setRestoring] = useState<BackupListItem | null>(null);
  const [configuring, setConfiguring] = useState(false);

  const data = overview.data;
  const names = new Map((data?.servers ?? []).map((item) => [item.id ?? 'none', item.name]));
  const totalProblems = (data?.servers ?? []).reduce((sum, item) => sum + item.problems, 0);
  const mayDownload = can(roles, 'secrets.manage');
  const mayRestore = can(roles, 'backups.restore_production');
  const mayConfigure = can(roles, 'backups.delete');

  return (
    <div className="page">
      <PageHeader
        title="Резервные копии"
        subtitle={data ? `По серверам и сайтам · до ${data.copiesPerSite} копий на сайт, новая заменяет самую старую` : 'По серверам и сайтам'}
        actions={
          mayConfigure && data ? (
            <Button icon={Settings2} onClick={() => setConfiguring(true)}>
              Копий на сайт: {data.copiesPerSite}
            </Button>
          ) : null
        }
      />

      {overview.isPending ? (
        <CardGridSkeleton cards={3} />
      ) : overview.isError ? (
        <ErrorState error={overview.error} />
      ) : data ? (
        <div className="stack">
          <Storage overview={data} />

          {data.servers.length > 0 ? (
            <div className="server-tiles" role="group" aria-label="Серверы">
              <ServerTile
                server={null}
                active={server === null}
                onPick={() => setServer(null)}
                totals={{
                  servers: data.servers.filter((item) => item.id !== null).length,
                  sites: data.servers.reduce((sum, item) => sum + item.siteCount, 0),
                  copies: data.servers.reduce((sum, item) => sum + item.copyCount, 0),
                  bytes: data.servers.reduce((sum, item) => sum + Number(item.sizeBytes), 0),
                  problems: totalProblems,
                }}
              />
              {data.servers.map((item) => (
                <ServerTile key={item.id ?? 'none'} server={item} active={server === (item.id ?? 'none')} onPick={() => setServer(item.id ?? 'none')} />
              ))}
            </div>
          ) : null}

          <div className="row-between row-wrap">
            <div className="row-tight row-wrap">
              <SearchInput label="Найти сайт" placeholder="Сайт или домен" value={search} onChange={setSearch} />
              <SubTabs
                label="Какие сайты"
                prefix="backups-"
                active={problems}
                onChange={setProblems}
                tabs={[
                  { id: 'all', label: 'Все' },
                  { id: 'problems', label: 'С проблемами', ...(totalProblems > 0 ? { count: totalProblems } : {}) },
                ]}
              />
            </div>
            {sites.data ? (
              <span className="text-sm muted">
                {sites.data.limited ? `Показаны первые ${sites.data.sites.length} из ${sites.data.total} — уточните поиск` : `Сайтов: ${sites.data.sites.length}`}
              </span>
            ) : null}
          </div>

          {sites.isPending ? (
            <CardGridSkeleton cards={3} />
          ) : sites.isError ? (
            <ErrorState error={sites.error} />
          ) : sites.data.sites.length === 0 ? (
            <Card>
              <EmptyState icon={Archive} message={q || problems === 'problems' || server ? 'Ничего не нашлось' : 'Сайтов с копиями пока нет. Копии создаются по расписанию для сайтов на подключённых серверах.'} />
            </Card>
          ) : (
            <div className="stack">
              {sites.data.sites.map((site) => (
                <SiteCopies
                  key={site.siteId}
                  site={site}
                  copiesPerSite={data.copiesPerSite}
                  serverName={server === null ? (names.get(site.serverId ?? 'none') ?? null) : null}
                  mayDownload={mayDownload}
                  mayRestore={mayRestore}
                  onRestore={setRestoring}
                />
              ))}
            </div>
          )}
        </div>
      ) : null}

      {restoring ? <RestoreToHostDialog backup={restoring} onClose={() => setRestoring(null)} /> : null}
      {configuring && data ? <BackupSettingsDialog current={data.copiesPerSite} onClose={() => setConfiguring(false)} /> : null}
    </div>
  );
}
